import type { FC, ReactElement, ReactNode } from 'react'

import { Children, cloneElement, isValidElement } from 'react'
import { cn } from '@/lib/utils'

export const inputClass = 'h-11 w-full border border-input bg-background/60 px-3 text-sm text-foreground placeholder:text-subtle transition focus:border-lime focus:outline-none disabled:opacity-60'

export const textareaClass = 'w-full border border-input bg-background/60 px-3 py-2.5 text-sm leading-relaxed text-foreground placeholder:text-subtle transition focus:border-lime focus:outline-none'

export const selectClass = 'h-11 w-full appearance-none border border-input bg-background/60 px-3 pr-9 text-sm text-foreground transition focus:border-lime focus:outline-none'

const CONTROLS = ['input', 'select', 'textarea']

interface FieldProps {
    label: string
    htmlFor: string
    hint?: ReactNode
    error?: string | null
    children: ReactNode
    className?: string
}

// Label, control and message. When the control is a direct input, select or
// textarea, its message is linked with aria-describedby so screen readers
// announce the hint or the error when the field gets focus.
export const Field: FC<FieldProps> = ({ label, htmlFor, hint, error, children, className }): ReactNode => {
    const messageId = `${htmlFor}-message`
    const hasMessage = Boolean(error || hint)

    const control = Children.map(children, (child) => {
        if (!hasMessage || !isValidElement(child) || typeof child.type !== 'string' || !CONTROLS.includes(child.type)) return child

        return cloneElement(child as ReactElement<{ 'aria-describedby'?: string }>, { 'aria-describedby': messageId })
    })

    return (
        <div className={cn('flex flex-col gap-2', className)}>
            <label htmlFor={htmlFor} className='font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground'>
                {label}
            </label>

            {control}

            {error
                ? <p id={messageId} className='text-xs text-coral' role='alert'>{error}</p>
                : hint ? <p id={messageId} className='text-xs text-subtle'>{hint}</p> : null}
        </div>
    )
}

interface FormMessageProps {
    tone: 'error' | 'success'
    children: ReactNode
}

export const FormMessage: FC<FormMessageProps> = ({ tone, children }): ReactNode => {
    return (
        <p
            role={tone === 'error' ? 'alert' : 'status'}
            className={cn('border px-3 py-2.5 text-sm', tone === 'error' ? 'border-coral/40 bg-coral/10 text-coral' : 'border-lime/30 bg-lime/10 text-lime')}
        >
            {children}
        </p>
    )
}
